// Demo Steps Configuration for Different Pages
// 
// HOW TO ADD A NEW PAGE TOUR:
// 1. Add step configurations to the appropriate file in /DemoSteps/ folder
// 2. Import the steps in this file 
// 3. Add to the `demoStepsConfig` object with a unique page identifier
// 4. Use the page identifier when adding DemoWidget to your Vue component
// 5. Each page tour is tracked separately in the database
//
// STEP OBJECT FORMAT:
// {
//   title: "Step Title",
//   content: "HTML content with explanations",
//   info: "Optional additional info or tips", 
//   target: "data-demo-target-value" or null for no highlight
// }

// Import demo steps from organized files
import {
  propertiesIndexSteps,
  propertiesCreateSteps,
  propertiesEditSteps,
  propertiesShowSteps,
  propertiesApplicationSteps,
  propertiesApplicationDetailsSteps,
  propertiesGuestApplicationSteps
} from './DemoSteps/propertiesSteps.js'

import {
  insurancePropertiesSteps,
  insuranceCreatePropertySteps,
  insuranceEditPropertySteps
} from './DemoSteps/insuranceSteps.js'

import {
  maintenanceIndexSteps,
  maintenanceCreateSteps,
  maintenanceEditSteps,
  maintenanceEmergencyOptionSteps,
  maintenanceSubscriptionIndexSteps,
  maintenanceTermsAndConditionsSteps
} from './DemoSteps/maintenanceSteps.js'

import {
  rentedPropertiesIndexSteps,
  rentedPropertiesCreateSteps,
  rentedPropertiesEditSteps,
  rentedPropertiesLeaseInformationSteps,
  rentedPropertiesLeaseInformationEditSteps,
  rentedPropertiesPaymentSteps,
  rentedPropertiesPaymentSucceededSteps,
  rentedPropertiesPaymentFailedSteps,
  rentedPropertiesSubscriptionIndexSteps
} from './DemoSteps/rentedPropertiesSteps.js'

// Legacy steps for other pages (to be organized later)
export const applicantsIndexSteps = [
  {
    title: "Welcome to Applicants!",
    content: `<p class="mb-3">Welcome to your Applicants dashboard!</p><p>This is where you can review and manage all rental applications.</p>`,
    info: "Let's explore the key features of this page.",
    target: null
  },
  // Add more steps as needed for applicants page
]

export const reportsIndexSteps = [
  {
    title: "Welcome to Reports!",
    content: `<p class="mb-3">Welcome to your Reports dashboard!</p><p>Generate and view detailed reports about your properties and applications.</p>`,
    info: "Learn how to create and manage reports.",
    target: null
  },
  // Add more steps as needed for reports page
]

export const settingsIndexSteps = [
  {
    title: "Welcome to Settings!",
    content: `<p class="mb-3">Welcome to your Settings page!</p><p>Customize your account preferences and application settings.</p>`,
    info: "Configure your account to match your workflow.",
    target: null
  },
  // Add more steps as needed for settings page
]

// Demo steps configuration mapping - Add new pages here
export const demoStepsConfig = {
  // Properties pages
  'properties.index': propertiesIndexSteps,
  'properties.create': propertiesCreateSteps,
  'properties.edit': propertiesEditSteps,
  'properties.show': propertiesShowSteps,
  'properties.application': propertiesApplicationSteps,
  'properties.application-details': propertiesApplicationDetailsSteps,
  'properties.guest-application': propertiesGuestApplicationSteps,
  
  // Insurance pages
  'insurance.properties': insurancePropertiesSteps,
  'insurance.create-property': insuranceCreatePropertySteps,
  'insurance.edit-property': insuranceEditPropertySteps,
  
  // Maintenance pages
  'maintenance.index': maintenanceIndexSteps,
  'maintenance.create': maintenanceCreateSteps,
  'maintenance.edit': maintenanceEditSteps,
  'maintenance.emergency-option': maintenanceEmergencyOptionSteps,
  'maintenance.subscription-index': maintenanceSubscriptionIndexSteps,
  'maintenance.terms-and-conditions': maintenanceTermsAndConditionsSteps,
  
  // Rented Properties pages
  'rented-properties.index': rentedPropertiesIndexSteps,
  'rented-properties.create': rentedPropertiesCreateSteps,
  'rented-properties.edit': rentedPropertiesEditSteps,
  'rented-properties.lease-information': rentedPropertiesLeaseInformationSteps,
  'rented-properties.edit-lease-information': rentedPropertiesLeaseInformationEditSteps,
  'rented-properties.payment': rentedPropertiesPaymentSteps,
  'rented-properties.payment-succeeded': rentedPropertiesPaymentSucceededSteps,
  'rented-properties.payment-failed': rentedPropertiesPaymentFailedSteps,
  'rented-properties.subscription-index': rentedPropertiesSubscriptionIndexSteps,
  
  // Legacy pages (to be organized)
  'applicants.index': applicantsIndexSteps,
  'reports.index': reportsIndexSteps,
  'settings.index': settingsIndexSteps,
}

// Helper function to get demo steps for a page
export function getDemoSteps(pageIdentifier) {
  return demoStepsConfig[pageIdentifier] || []
}

// Helper function to check if a page has demo steps configured
export function hasDemo(pageIdentifier) {
  return pageIdentifier in demoStepsConfig && demoStepsConfig[pageIdentifier].length > 0
}

// Helper function to get all available page identifiers
export function getAvailablePages() {
  return Object.keys(demoStepsConfig)
}

// Helper function to get page statistics
export function getPageStats() {
  return Object.entries(demoStepsConfig).map(([pageId, steps]) => ({
    pageIdentifier: pageId,
    stepCount: steps.length,
    hasSteps: steps.length > 0
  }))
}